/**
 * Pure helpers for the "Instrument" timeline ruler (DESIGN.md Aneks M002
 * §A1–§A2). No DOM access — positioning/label decisions are computed here
 * and the UI layer (PlaybackBar) only paints the result. Kept independent of
 * the system timezone: callers pass an explicit UTC-minute offset so tests
 * are deterministic regardless of the machine running them.
 */

/** Step between "round" clock-time major ticks, per §A1. */
export const MAJOR_TICK_STEP_MINUTES = 15;

/** Minimum distance (px) from either track edge for a major tick's label to be shown (§A1 collision rule). */
export const EDGE_LABEL_GAP_PX = 28;

/**
 * Proportional position of `ms` within [domainStartMs, domainEndMs], clamped
 * to [0, 1]. Returns 0 for a degenerate (zero-width or inverted) domain.
 */
export function fracOfTime(ms: number, domainStartMs: number, domainEndMs: number): number {
  if (domainEndMs <= domainStartMs) return 0;
  const frac = (ms - domainStartMs) / (domainEndMs - domainStartMs);
  return Math.max(0, Math.min(1, frac));
}

/**
 * Index of the verified frame nearest to `targetMs` by |Δt|. Ties resolve to
 * the earlier candidate encountered (the OLDER of the two, since frames are
 * ascending and later entries only replace on a *strictly smaller* delta).
 * Returns -1 for an empty list.
 */
export function nearestFrameIndexByTime(frameTimes: number[], targetMs: number): number {
  if (frameTimes.length === 0) return -1;
  let bestIdx = 0;
  let bestDelta = Math.abs(frameTimes[0] - targetMs);
  for (let i = 1; i < frameTimes.length; i++) {
    const delta = Math.abs(frameTimes[i] - targetMs);
    if (delta < bestDelta) {
      bestDelta = delta;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export interface MajorTick {
  /** Real epoch-ms (UTC) position of the tick — used for frac() positioning. */
  ms: number;
  /** "HH:MM" wall-clock label in the requested zone. */
  label: string;
}

/**
 * Round clock-time ticks (every `stepMinutes`, default 15) that fall inside
 * [domainStartMs, domainEndMs], in the zone described by `offsetMinutes`
 * (minutes to ADD to a UTC-ms timestamp to get that zone's wall clock —
 * e.g. `-new Date().getTimezoneOffset()` for the browser's local zone, `0`
 * for UTC). Independent of the host machine's timezone: the label is derived
 * from the shifted timestamp's UTC getters, never the environment's local
 * zone.
 */
export function computeMajorTicks(
  domainStartMs: number,
  domainEndMs: number,
  offsetMinutes: number,
  stepMinutes: number = MAJOR_TICK_STEP_MINUTES
): MajorTick[] {
  if (domainEndMs <= domainStartMs || stepMinutes <= 0) return [];
  const stepMs = stepMinutes * 60_000;
  const offsetMs = offsetMinutes * 60_000;
  const shiftedStart = domainStartMs + offsetMs;
  const shiftedEnd = domainEndMs + offsetMs;
  const firstShifted = Math.ceil(shiftedStart / stepMs) * stepMs;

  const ticks: MajorTick[] = [];
  for (let shifted = firstShifted; shifted <= shiftedEnd; shifted += stepMs) {
    ticks.push({ ms: shifted - offsetMs, label: formatShiftedHHMM(shifted) });
  }
  return ticks;
}

function formatShiftedHHMM(shiftedMs: number): string {
  const d = new Date(shiftedMs);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export interface PositionedTick extends MajorTick {
  /** [0,1] position on the track. */
  frac: number;
  /** False when the tick sits within EDGE_LABEL_GAP_PX of either edge — the tick mark is still drawn, only the label is skipped. */
  showLabel: boolean;
}

/**
 * Attach a track fraction and a label-collision verdict to each major tick.
 * `trackWidthPx` is the track's rendered pixel width at the moment of
 * layout; ticks within `edgeGapPx` of either edge suppress their label.
 */
export function positionMajorTicks(
  ticks: MajorTick[],
  domainStartMs: number,
  domainEndMs: number,
  trackWidthPx: number,
  edgeGapPx: number = EDGE_LABEL_GAP_PX
): PositionedTick[] {
  return ticks.map((t) => {
    const frac = fracOfTime(t.ms, domainStartMs, domainEndMs);
    const px = frac * trackWidthPx;
    const nearEdge = trackWidthPx > 0 && (px < edgeGapPx || px > trackWidthPx - edgeGapPx);
    return { ...t, frac, showLabel: !nearEdge };
  });
}

/** `aria-valuetext` for the scrubber (§A1): "HH:MM, najnowsza obserwacja" or "HH:MM, N min przed najnowszą". */
export function formatScrubberValueText(clockLabel: string, minutesBeforeNewest: number): string {
  if (minutesBeforeNewest <= 0) return `${clockLabel}, najnowsza obserwacja`;
  return `${clockLabel}, ${minutesBeforeNewest} min przed najnowszą`;
}

/**
 * The HISTORIA/TERAZ frame label (§A3). `compact` (§A8, ≤360px) drops the
 * "HISTORIA · " prefix, keeping only the relative minutes.
 */
export function formatHistoryLabel(minutesBeforeNewest: number, compact: boolean): string {
  const minutes = Math.max(0, Math.round(minutesBeforeNewest));
  return compact ? `−${minutes} min` : `HISTORIA · −${minutes} min`;
}
